import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Topbar from "@/components/Topbar";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { CurrencyDollar, CheckCircle, ArrowUUpLeft, WarningOctagon, Clock, Receipt, DownloadSimple } from "@phosphor-icons/react";

/**
 * v3.2.33 · Payment history / earnings ledger. One page, two modes —
 * clients see money spent, PEs see money earned. Both roles share the
 * same visual grammar so the ledger feels like a single product.
 */
export default function PaymentHistoryPage() {
  const { user } = useAuth();
  const isPe = user?.role === "pe";
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(null);   // PE-only lifetime earnings
  const [count, setCount] = useState(null);
  const [tab, setTab] = useState("all");
  const [busy, setBusy] = useState(true);
  const [modeBanner, setModeBanner] = useState("");

  useEffect(() => {
    (async () => {
      setBusy(true);
      try {
        // v3.2.34 · Fire alongside the data load — small, cached.
        const modeReq = api.get("/payments/mode").catch(() => null);
        if (isPe) {
          const { data } = await api.get("/payments/earnings");
          setRows(data.rows || []);
          setTotal(data.total_earnings_usd);
          setCount(data.count);
        } else {
          const { data } = await api.get("/payments/history");
          setRows(data.rows || []);
        }
        const m = await modeReq;
        if (m?.data?.banner) setModeBanner(m.data.banner);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Load failed");
      } finally { setBusy(false); }
    })();
  }, [isPe]);

  const filtered = rows.filter((r) => {
    if (tab === "all") return true;
    if (tab === "paid") return r.payment_status === "paid";
    if (tab === "refunded") return r.payment_status === "refunded";
    if (tab === "disputed") return r.dispute_status === "open";
    if (tab === "pending") return r.payment_status === "pending" || r.status === "initiated";
    return true;
  });

  const clientSpend = rows
    .filter((r) => r.payment_status === "paid")
    .reduce((s, r) => s + Number(r.amount_usd || 0), 0);

  const statusChip = (r) => {
    if (r.dispute_status === "open") {
      return { cls: "border-[#FF3B30]/50 text-[#FF3B30] bg-[#FF3B30]/10",
                 icon: <WarningOctagon size={11} weight="fill" />, label: "disputed" };
    }
    if (r.payment_status === "refunded") {
      return { cls: "border-gray-500/40 text-gray-400 bg-black/30",
                 icon: <ArrowUUpLeft size={11} />, label: "refunded" };
    }
    if (r.payment_status === "paid") {
      return { cls: "border-emerald-400/50 text-emerald-400 bg-emerald-400/10",
                 icon: <CheckCircle size={11} weight="fill" />, label: "paid" };
    }
    return { cls: "border-[#FFCC00]/40 text-[#FFCC00] bg-[#FFCC00]/10",
              icon: <Clock size={11} />, label: r.payment_status || "pending" };
  };

  return (
    <div className="industrial min-h-screen bg-[#0A0C10]">
      <Topbar active="payments" />
      {modeBanner && (
        <div className="border-b border-[#FFCC00]/30 bg-[#FFCC00]/10 py-2 text-center font-mono text-xs text-[#FFCC00] uppercase tracking-widest"
             data-testid="payments-sandbox-banner">
          {modeBanner}
        </div>
      )}
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-10">
        <div className="label-tag mb-2">// {isPe ? "earnings" : "payments"}</div>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter"
            data-testid="payments-heading">
          {isPe ? "Earnings ledger" : "Payment history"}
        </h1>

        {/* Stat cards */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-3">
          {isPe ? (
            <>
              <StatCard label="Lifetime earned"
                value={total != null ? `$${total.toLocaleString()}` : "—"}
                sub="USD · net of 10% platform fee"
                accent="#00E5FF" testId="stat-earnings-total" />
              <StatCard label="Paid milestones" value={count ?? "—"}
                sub="settled to your ledger" testId="stat-earnings-count" />
              <StatCard label="Awaiting"
                value={rows.filter((r) => r.payment_status !== "paid").length}
                sub="pending / initiated / refunded"
                testId="stat-earnings-awaiting" />
            </>
          ) : (
            <>
              <StatCard label="Lifetime paid"
                value={`$${clientSpend.toLocaleString()}`}
                sub={`USD across ${rows.filter((r) => r.payment_status === "paid").length} milestones`}
                accent="#00E5FF" testId="stat-payments-total" />
              <StatCard label="Refunded"
                value={rows.filter((r) => r.payment_status === "refunded").length}
                sub={`${rows.reduce((s, r) => s + (r.payment_status === "refunded" ? Number(r.amount_usd || 0) : 0), 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} USD reversed`}
                testId="stat-payments-refunded" />
              <StatCard label="Disputes"
                value={rows.filter((r) => r.dispute_status === "open").length}
                sub="open card-issuer disputes"
                accent={rows.some((r) => r.dispute_status === "open") ? "#FF3B30" : undefined}
                testId="stat-payments-disputes" />
            </>
          )}
        </div>

        {/* Tabs */}
        <div className="mt-8 flex gap-2 border-b border-white/10 overflow-x-auto">
          {["all", "paid", "refunded", "disputed", "pending"].map((k) => (
            <button key={k}
              onClick={() => setTab(k)}
              className={`px-3 py-2 text-xs font-mono uppercase tracking-wider transition-colors whitespace-nowrap ${
                tab === k ? "text-[#00E5FF] border-b-2 border-[#00E5FF]" : "text-gray-500 hover:text-white"
              }`}
              data-testid={`payments-tab-${k}`}>
              {k}
            </button>
          ))}
        </div>

        {busy ? (
          <div className="mt-6 text-gray-500 font-mono text-sm" data-testid="payments-loading">
            Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div className="mt-6 border border-white/10 bg-white/5 p-8 text-center" data-testid="payments-empty">
            <Receipt size={26} className="mx-auto text-gray-500" />
            <div className="mt-3 text-sm font-mono text-gray-400">
              {tab === "all"
                ? (isPe ? "No paid milestones yet — bid on a job to get started." : "No payments yet.")
                : `No ${tab} entries.`}
            </div>
          </div>
        ) : (
          <div className="mt-6 border border-white/10" data-testid="payments-table">
            <div className="hidden md:grid grid-cols-[2fr_120px_120px_120px_100px_60px] gap-2 px-4 py-2 border-b border-white/10 bg-black/40 font-mono text-[10px] uppercase tracking-wider text-gray-500">
              <div>Job · Milestone</div>
              <div>Amount</div>
              <div>{isPe ? "Earned" : "Fee"}</div>
              <div>Date</div>
              <div>Status</div>
              <div>Receipt</div>
            </div>
            {filtered.map((r) => {
              const chip = statusChip(r);
              const when = r.paid_at || r.created_at;
              return (
                <div key={r.session_id}
                  className="grid grid-cols-1 md:grid-cols-[2fr_120px_120px_120px_100px_60px] gap-2 px-4 py-3 border-b border-white/5 items-center hover:bg-white/[0.02]"
                  data-testid={`payments-row-${r.session_id}`}>
                  <div className="min-w-0">
                    <Link to={`/jobs/${r.job_id}`}
                      className="text-white text-sm truncate hover:text-[#00E5FF] block">
                      MS{r.milestone_idx} · {r.milestone_title || <span className="italic text-gray-500">(untitled)</span>}
                    </Link>
                    <div className="text-[10px] font-mono text-gray-500 truncate">
                      {r.job_title}
                    </div>
                  </div>
                  <div className="font-display font-black tabular-nums text-white">
                    ${Number(r.amount_usd || 0).toLocaleString()}
                    <div className="text-[9px] font-mono text-gray-500">
                      {r.currency !== "USD" && r.client_amount ? `${r.currency} ${Number(r.client_amount).toLocaleString()}` : "USD"}
                    </div>
                  </div>
                  <div className="text-sm text-gray-300 tabular-nums">
                    {isPe
                      ? <>${Number(r.pe_earnings_usd || 0).toLocaleString()}<div className="text-[9px] font-mono text-gray-500">to you</div></>
                      : <>${Number(r.platform_fee_usd || 0).toLocaleString()}<div className="text-[9px] font-mono text-gray-500">platform</div></>}
                  </div>
                  <div className="text-xs font-mono text-gray-400">
                    {when ? new Date(when).toLocaleDateString() : "—"}
                  </div>
                  <div>
                    <span className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 border ${chip.cls}`}
                          data-testid={`payments-status-${r.session_id}`}>
                      {chip.icon} {chip.label}
                    </span>
                  </div>
                  {/* v3.2.36 · Hosted receipt link — Stripe-issued invoice URL. */}
                  <div>
                    {r.hosted_invoice_url ? (
                      <a href={r.hosted_invoice_url} target="_blank" rel="noopener noreferrer"
                         className="inline-flex items-center gap-1 text-[10px] font-mono text-[#00E5FF] hover:text-white uppercase tracking-wider"
                         data-testid={`payments-receipt-${r.session_id}`}
                         title="Open Stripe hosted receipt">
                        <DownloadSimple size={12} /> pdf
                      </a>
                    ) : (
                      <span className="text-[10px] font-mono text-gray-600">—</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, accent = "#FFFFFF", testId }) {
  return (
    <div className="border border-white/10 bg-white/[0.02] p-4" data-testid={testId}>
      <div className="text-[9px] font-mono uppercase tracking-wider text-gray-500">{label}</div>
      <div className="mt-1 font-display font-black text-2xl tabular-nums" style={{ color: accent }}>
        {value}
      </div>
      <div className="text-[10px] font-mono text-gray-500 mt-1">{sub}</div>
    </div>
  );
}
